import { useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import HelpPanel from "./HelpPanel";
import {
  LayoutDashboard, UtensilsCrossed, Users, Receipt, BarChart3,
  ScrollText, Settings, Percent, LogOut
} from "lucide-react";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/menu", icon: UtensilsCrossed, label: "Menu" },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/taxes", icon: Percent, label: "Tax Rates" },
  { to: "/admin/transactions", icon: Receipt, label: "Transactions" },
  { to: "/admin/reports", icon: BarChart3, label: "Reports" },
  { to: "/admin/audit", icon: ScrollText, label: "Audit Log" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

export default function AdminLayout() {
  const { adminUser, adminLogout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!adminUser) navigate("/admin/login");
  }, [adminUser, navigate]);

  if (!adminUser) return null;

  return (
    <div className="flex min-h-screen bg-white" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar flex flex-col justify-between" data-testid="admin-sidebar">
        <div>
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-heading text-lg font-bold tracking-tight">Minibar POS</h2>
            <p className="text-[10px] text-gray-400 uppercase tracking-[0.2em] font-bold mt-0.5">Administration</p>
          </div>
          <nav className="mt-2 flex flex-col gap-0.5 px-2" data-testid="admin-nav">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-none transition-colors duration-150 ${
                    isActive ? "bg-black text-white" : "text-gray-600 hover:bg-gray-100 hover:text-black"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="w-4 h-4" aria-hidden="true" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="p-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 mb-2 truncate">{adminUser?.display_name || 'Admin'}</div>
          <button
            onClick={() => { adminLogout(); navigate('/admin/login'); }}
            className="flex items-center gap-2 text-xs text-gray-400 hover:text-red-500 transition-colors"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-[#FAFAFA]">
        <div className="p-6 lg:p-8 max-w-7xl">
          <Outlet />
        </div>
      </main>
      <HelpPanel />
    </div>
  );
}
